import { changePassword } from "@/modules/user/api/user.api";
import { PasswordInput } from "@/components/password-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState } from "react";
import { toast } from "sonner";

const formSchema = z
  .object({
    currentPassword: z.string().min(1, {
      message: "Mật khẩu cũ không được để trống.",
    }),
    newPassword: z
      .string()
      .min(8, {
        message: "Mật khẩu mới phải từ 8 đến 72 ký tự.",
      })
      .max(72, {
        message: "Mật khẩu mới phải từ 8 đến 72 ký tự.",
      })
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/, {
        message: "Mật khẩu phải có chữ hoa, chữ thường, số và ký tự đặc biệt.",
      }),
    confirmPassword: z.string().min(1, {
      message: "Mật khẩu xác nhận không được để trống.",
    }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Mật khẩu xác nhận không khớp.",
    path: ["confirmPassword"],
  });

export function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setLoading(true);
      await changePassword(values);
      toast.success("Đổi mật khẩu thành công");
      form.reset();
      setOpen(false);
    } catch (error: any) {
      console.error("Đổi mật khẩu thất bại:", error);
      const errCode = error?.data?.code || error?.response?.data?.code;
      if (errCode === 1009) {
        form.setError("currentPassword", {
          type: "manual",
          message: "Mật khẩu cũ không chính xác.",
        });
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" variant="outline">
          <Plus className="w-4 h-4" />
          Đổi mật khẩu
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Đổi mật khẩu</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mật khẩu cũ</FormLabel>
                <FormControl>
                  <PasswordInput placeholder="Mật khẩu cũ" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mật khẩu mới</FormLabel>
                <FormControl>
                  <PasswordInput placeholder="Mật khẩu mới" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mật khẩu xác nhận</FormLabel>
                <FormControl>
                  <PasswordInput placeholder="Mật khẩu xác nhận" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              className="w-1/2"
              disabled={loading}
              onClick={() => {
                setOpen(false);
                form.reset();
              }}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              className="w-1/2"
              disabled={loading}
              onClick={form.handleSubmit(onSubmit)}
            >
              {loading ? "Đang lưu..." : "Lưu"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
